import { Inject, Injectable, LOCALE_ID } from "@angular/core";
import { HOURS_OF_THE_DAY } from "@obg/ngx.common";
import { DateService } from "@obg/ngx.global";
import { isToday, isValid, startOfTomorrow } from "date-fns";

@Injectable({
	providedIn: "root",
})
export class DepositLimitCalculatorDateService {
	private readonly millisecsToMins = 6e4;
	private readonly englishLocale = "en-US";
	private readonly utcTimeZone = "UTC";

	constructor(
		private dateService: DateService,
		@Inject(LOCALE_ID) public locale: string
	) {}

	getMinimumStartDate(todaysDate: Date): Date {
		const isTodayAfterEleven = isToday(todaysDate) && todaysDate.getHours() === 23;
		return isTodayAfterEleven ? startOfTomorrow() : todaysDate;
	}

	createHoursAvailable(date: Date): string[] {
		const currentHour = new Date().getHours();
		return !isToday(date)
			? HOURS_OF_THE_DAY
			: HOURS_OF_THE_DAY.filter((hour: string) => {
					const hourInteger = parseInt(hour.slice(0, 2), 10);
					return hourInteger > currentHour;
				});
	}

	convertDateToUtc(dateToConvert: Date, time: string, timeZone: string) {
		const dateString = this.createDateString(dateToConvert, true);
		const isValidTimeZone = this.isValidTimeZone(timeZone);

		if (!isValidTimeZone) {
			return { error: "Invalid Params" };
		}

		const offset = this.getTimeZoneOffset(dateToConvert, timeZone);
		const dateTime = `${dateString}T${time}:00${offset}`;
		const convertedDate = new Date(dateTime);
		const isValidDate = isValid(convertedDate);

		return !isValidDate ? { error: "Invalid Params" } : { convertedDate };
	}

	createDateString(date: Date, isUtc = false): string {
		const month = this.padString(date.getMonth() + 1);
		const day = this.padString(date.getDate());
		const year = date.getFullYear();
		return !isUtc ? `${year}/${month}/${day}` : `${year}-${month}-${day}`;
	}

	getTimeZoneOffset = (dateInput = new Date(), timeZone: string): string => {
		const offsetMinutes = this.getOffsetInMinutes(dateInput, timeZone);
		const offsetHours = Math.floor(offsetMinutes / 60);
		// convert the offset from eg. 120 minutes to +02:00
		return `${this.getPrefix(offsetHours)}${this.formatHours(offsetHours)}:${this.formatMinutes(
			offsetMinutes,
			offsetHours
		)}`;
	};

	getOffsetInMinutes(dateInput: Date, timeZone: string): number {
		const utcDate = new Date(dateInput.toLocaleString(this.englishLocale, { timeZone: this.utcTimeZone }));
		const tzDate = new Date(dateInput.toLocaleString(this.englishLocale, { timeZone }));
		return (tzDate.getTime() - utcDate.getTime()) / this.millisecsToMins;
	}

	createDateAndTimeString(date: Date, time?: string | undefined) {
		const dateString = this.createDateString(date);
		return new Date(`${dateString}, ${time ?? "00:00"}:00`);
	}

	isValidTimeZone(tz: string): boolean {
		try {
			Intl.DateTimeFormat(undefined, { timeZone: tz });
			return true;
		} catch {
			return false;
		}
	}

	formatTime(date: Date): string {
		const dateString = date.toLocaleTimeString(this.locale);
		// 3 matching groups:
		// ([\d]+:[\d]{2}) - Hour:Minute
		// (:[\d]{2}) - Seconds
		// (.*) - the space and period (AM/PM)
		// Then we display the 1st and 3rd groups
		return dateString.replace(/([\d]+:[\d]{2})(:[\d]{2})(.*)/, "$1$3");
	}

	formatDate(date: Date, formatType: string, timeZone: string, time?: string): string | null {
		const dateAndTime = this.createDateAndTimeString(date, time);
		return this.dateService.format(dateAndTime, formatType, timeZone);
	}

	formatMinutes(offsetMinutes: number, offsetHours: number): string {
		return this.padString((offsetMinutes - 60 * offsetHours).toString());
	}

	formatHours(offsetHours: number): string {
		return this.padString(Math.abs(offsetHours).toString());
	}

	getPrefix(offsetHours: number): string {
		return offsetHours >= 0 ? "+" : "-";
	}

	padString(stringValue: any): string {
		return stringValue.toString().padStart(2, "0");
	}
}
